import { useCallback, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import {
  Button,
  Card,
  CardBody,
  CardHeader,
  InputGroup,
  useLatinCharacterValidation
} from '@/common'
import { organizationDetailsSchema } from '@/features/organization/api/OrganizationModel'
import { useDoesOrganizationRequiresLocalAddress } from '@/shared/hooks/api/organizations'
import ReactCountryFlag from 'react-country-flag'
import NonEuCountrySelector from '@/components/NonEuCountrySelector'

export default function OrganizationDetailsPage({
  next,
  initialState
}: {
  next: (payload: {
    placeOfRegistration: string
    organizationName: string
    localName: string
    // businessRegistrationNumber: string // TODO: add back later
  }) => void
  initialState: {
    placeOfRegistration: string
    organizationName: string
    localName: string
    // businessRegistrationNumber: string // TODO: add back later
  }
}) {
  const { t } = useTranslation()
  const [state, setState] = useState(initialState)
  const { placeOfRegistration, organizationName, localName } = state
  const doesOrganizationRequiresLocalAddress =
    useDoesOrganizationRequiresLocalAddress(placeOfRegistration)

  const onInputChange = useCallback(
    (evt: React.ChangeEvent<HTMLInputElement> | React.ChangeEvent<HTMLSelectElement>) => {
      if (!doesOrganizationRequiresLocalAddress && evt.target.id === 'organizationName') {
        setState({ ...state, organizationName: evt.target.value, localName: evt.target.value })
        return
      }
      setState({ ...state, [evt.target.id]: evt.target.value })
    },
    [state, doesOrganizationRequiresLocalAddress]
  )

  const onPlaceOfRegistrationChange = useCallback(
    (value: string) => {
      setState({ ...state, placeOfRegistration: value, localName: '', organizationName: '' })
    },
    [state]
  )

  const onNextClick = useCallback(() => {
    return next(state)
  }, [state, next])

  const isNextEnabled = useMemo(() => {
    return organizationDetailsSchema.safeParse(state).success
  }, [state])

  const { error: organizationNameError } = useLatinCharacterValidation(
    organizationName,
    t('common:errors.latinCharacters'),
    undefined
  )

  return (
    <>
      <Card>
        <CardHeader>{t('onboarding:steps.organizationDetails')}</CardHeader>
        <div className="sm:flex sm:gap-2 sm:items-end">
          <div className="sm:w-3/5 mb-2 sm:mb-0">
            <CardBody className="space-y-4">
              <NonEuCountrySelector
                label={t('onboarding:inputFields.placeOfRegistration.label')}
                value={placeOfRegistration}
                onChange={onPlaceOfRegistrationChange}
                tooltip={t('onboarding:inputFields.placeOfRegistration.tooltip')}
              />
              {!doesOrganizationRequiresLocalAddress ? (
                <InputGroup
                  id="organizationName"
                  label={t('onboarding:inputFields.organizationName.label')}
                  disabled={placeOfRegistration.length <= 0}
                  value={organizationName}
                  onChange={onInputChange}
                  error={organizationNameError || undefined}
                  type="text"
                  hint={t('onboarding:inputFields.organizationName.hint')}
                />
              ) : (
                <>
                  <InputGroup
                    id="localName"
                    label={
                      <>
                        {t('onboarding:inputFields.localName.label')}
                        <ReactCountryFlag countryCode={placeOfRegistration} svg />
                      </>
                    }
                    value={localName}
                    onChange={onInputChange}
                    placeholder={t('onboarding:inputFields.localName.placeholder')}
                    type="text"
                    hint={t('onboarding:inputFields.organizationName.hint')}
                  />
                  <InputGroup
                    id="organizationName"
                    label={
                      <>
                        {t('onboarding:inputFields.organizationName.label')}
                        <ReactCountryFlag countryCode={'gb'} svg />
                      </>
                    }
                    disabled={placeOfRegistration.length <= 0}
                    value={organizationName}
                    onChange={onInputChange}
                    error={organizationNameError || undefined}
                    placeholder={t('onboarding:inputFields.organizationName.placeholder')}
                    type="text"
                    hint={t('onboarding:inputFields.requiresLocalNameHint')}
                  />
                </>
              )}
              {/* TODO: add back later
              <InputGroup
                id="businessRegistrationNumber"
                type="text"
                required
                label={<OperatorIdLabel countryCode={placeOfRegistration} />}
                value={businessRegistrationNumber}
                onChange={onInputChange}
              /> */}
            </CardBody>
          </div>
        </div>
      </Card>
      <div className="flex justify-end">
        <Button className="mt-4 w-24" onClick={onNextClick} disabled={!isNextEnabled}>
          {t('common:next')}
        </Button>
      </div>
    </>
  )
}
